import type { GetRiskAssessmentResultConfigVersionsResponseRow } from '@risksmart-app/trpc/src/types';
import {
  GetRiskAssessmentResultConfigVersionsDocument,
  type GetRiskAssessmentResultConfigVersionsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import type { ConfigType } from './types';

type UseGetVersionsArgs = {
  type: ConfigType;
};

const useGetVersionsBase = createQueryHook<
  UseGetVersionsArgs,
  GetRiskAssessmentResultConfigVersionsResponseRow[],
  GetRiskAssessmentResultConfigVersionsQuery
>({
  trpcQueryOptions: (trpc, { type }) =>
    trpc.frontend.riskAssessmentResultConfig.getVersions.queryOptions({
      Type: type,
    }),
  mapTrpcDataToGraphQL: (rows) => ({
    risk_assessment_result_config: (rows ?? []).map((r) => ({
      Id: r.Id,
      Version: r.Version,
      IsLatest: r.IsLatest,
      Config: r.Config,
      InitialConfig: r.InitialConfig,
      CreatedAtTimestamp: r.CreatedAtTimestamp,
      ModifiedAtTimestamp: r.ModifiedAtTimestamp,
      ConfigModifiedAtTimestamp: r.ConfigModifiedAtTimestamp,
      createdByUser: r.createdByUser
        ? { FriendlyName: r.createdByUser.FriendlyName }
        : null,
    })),
  }),
  graphqlDocument: GetRiskAssessmentResultConfigVersionsDocument,
  graphqlVariables: ({ type }) => ({ Type: type }),
  graphqlFetchPolicy: 'cache-and-network',
  graphqlPermission: 'read:risk_assessment_result_config',
});

export const useGetRiskAssessmentResultConfigVersions = (options: {
  type: ConfigType;
  skip?: boolean;
}) => {
  const { data, loading, error, refetch } = useGetVersionsBase({
    queryArgs: { type: options.type },
    shouldSkip: options.skip,
  });

  return {
    versions: data?.risk_assessment_result_config ?? [],
    loading,
    error,
    refetch,
  };
};
